<script setup lang="ts">
import { notificationsApi } from '@/services/notificationsApi';
import type { Notification } from '@/types/notifications';
import { Icon } from '@iconify/vue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useNotificationSound } from '@/composables/useNotificationSound';

const router = useRouter();

// Estado local – consulta direta à API (banco)
const notifications = ref<Notification[]>([]);
const unreadCount = ref(0);
const loading = ref(false);
const menuOpen = ref(false);

const hasUnread = computed(() => unreadCount.value > 0);

const sound = useNotificationSound();
// Baseline da sessão: a primeira leitura NÃO toca som; depois, toca quando o
// contador de não-lidas SOBE (qualquer módulo — chamados, pedidos, políticas...).
let lastCount: number | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;

/** Caminho interno do SPA (evita protocol-relative `//…` e esquemas tipo `javascript:`). */
function isInternalAppPath(raw: string | null | undefined): raw is string {
    if (!raw || typeof raw !== 'string') return false;
    const u = raw.trim();
    if (!u.startsWith('/') || u.startsWith('//')) return false;
    const pathOnly = u.split(/[?#]/)[0] ?? '';
    return pathOnly.length > 0 && !pathOnly.includes(':');
}

function applyMarkedRead(notificationId: number, updated: Notification): void {
    const index = notifications.value.findIndex((n) => n.id === notificationId);
    if (index !== -1) {
        notifications.value[index] = updated;
    }
    if (unreadCount.value > 0) unreadCount.value--;
    lastCount = unreadCount.value;
}

async function loadUnreadCount() {
    try {
        const count = await notificationsApi.getUnreadCount();
        if (lastCount !== null && count > lastCount) {
            sound.play();
            if (menuOpen.value) void loadNotifications();
        }
        lastCount = count;
        unreadCount.value = count;
    } catch (err) {
        console.error('Erro ao buscar contador de não lidas:', err);
    }
}

async function loadNotifications() {
    loading.value = true;
    try {
        const [result, count] = await Promise.all([
            notificationsApi.getNotifications({ unreadOnly: true, limit: 10 }),
            notificationsApi.getUnreadCount()
        ]);
        notifications.value = result.notifications;
        unreadCount.value = count;
        lastCount = count;
    } catch (err) {
        console.error('Erro ao carregar notificações:', err);
    } finally {
        loading.value = false;
    }
}

function formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) {
        return 'Agora mesmo';
    } else if (diffMin < 60) {
        return `Há ${diffMin} ${diffMin === 1 ? 'minuto' : 'minutos'}`;
    } else if (diffHour < 24) {
        return `Há ${diffHour} ${diffHour === 1 ? 'hora' : 'horas'}`;
    } else if (diffDay < 7) {
        return `Há ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`;
    } else {
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
}

async function handleMarkAsRead(notification: Notification) {
    if (notification.is_read) return;
    try {
        const updated = await notificationsApi.markAsRead(notification.id);
        applyMarkedRead(notification.id, updated);
    } catch (err) {
        console.error('Erro ao marcar como lida:', err);
    }
}

async function handleNotificationOpen(notification: Notification) {
    const url = notification.action_url;
    if (!isInternalAppPath(url)) return;
    if (!notification.is_read) {
        try {
            const updated = await notificationsApi.markAsRead(notification.id);
            applyMarkedRead(notification.id, updated);
        } catch (err) {
            console.error('Erro ao marcar como lida:', err);
        }
    }
    menuOpen.value = false;
    void router.push(url);
}

async function handleMarkAllAsRead() {
    try {
        await notificationsApi.markAllAsRead();
        notifications.value = [];
        unreadCount.value = 0;
        lastCount = 0;
    } catch (err) {
        console.error('Erro ao marcar todas como lidas:', err);
    }
}

onMounted(() => {
    loadUnreadCount();
    pollTimer = setInterval(loadUnreadCount, 30_000);
    window.addEventListener('focus', loadUnreadCount);
});

onUnmounted(() => {
    if (pollTimer) clearInterval(pollTimer);
    window.removeEventListener('focus', loadUnreadCount);
});

watch(menuOpen, (open) => {
    if (open) {
        loadNotifications();
    }
});
</script>

<template>
    <v-menu v-model="menuOpen" :close-on-content-click="false" class="notification_popup">
        <template v-slot:activator="{ props }">
            <v-btn icon flat v-bind="props" size="small" class="custom-hover-primary">
                <v-badge v-if="hasUnread" :content="unreadCount > 99 ? '99+' : unreadCount" color="error" offset-x="-4" offset-y="-6">
                    <div class="position-relative">
                        <div class="notify" v-if="hasUnread">
                            <span class="heartbit"></span>
                            <span class="point"></span>
                        </div>
                        <Icon icon="solar:bell-bing-line-duotone" height="24" width="24" />
                    </div>
                </v-badge>
                <div v-else class="position-relative">
                    <Icon icon="solar:bell-bing-line-duotone" height="24" width="24" />
                </div>
            </v-btn>
        </template>

        <v-sheet rounded="lg" width="385" elevation="10" class="mt-5 dropdown-box">
            <!-- Header -->
            <div class="px-5 pb-4 pt-6">
                <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                        <h6 class="text-h5 font-weight-semibold">Notificações</h6>
                        <v-chip v-if="hasUnread" color="primary" variant="flat" size="x-small" class="text-white ml-4" rounded="xl">
                            {{ unreadCount }} {{ unreadCount === 1 ? 'nova' : 'novas' }}
                        </v-chip>
                    </div>
                </div>
            </div>

            <!-- Lista de notificações -->
            <perfect-scrollbar style="height: 320px">
                <v-list class="py-0 theme-list" lines="two">
                    <v-list-item v-if="notifications.length === 0 && !loading" class="py-8 px-8 text-center">
                        <div class="d-flex flex-column align-center">
                            <Icon icon="solar:check-circle-line-duotone" height="48" width="48" class="text-success mb-3" />
                            <p class="text-subtitle-1 text-grey400">Nenhuma notificação pendente</p>
                        </div>
                    </v-list-item>

                    <template v-else>
                        <v-list-item
                            v-for="notification in notifications"
                            :key="notification.id"
                            :value="notification"
                            class="py-4 px-6 bg-grey100"
                            :class="{ 'notification-item--clickable': isInternalAppPath(notification.action_url) }"
                            @click="handleNotificationOpen(notification)"
                        >
                            <div class="flex-grow-1">
                                <div class="d-flex align-center justify-space-between mb-1">
                                    <h6 class="text-subtitle-1 font-weight-semibold text-primary">
                                        {{ notification.title }}
                                    </h6>
                                </div>

                                <p class="text-body-2 text-grey100 mb-0 w-full">
                                    {{ notification.message }}
                                    <span class="text-caption ml-2">{{ formatRelativeTime(notification.created_at) }}</span>
                                </p>
                            </div>

                            <template v-slot:append>
                                <v-btn
                                    icon
                                    variant="text"
                                    size="x-small"
                                    color="primary"
                                    @click.stop="handleMarkAsRead(notification)"
                                    title="Marcar como lida"
                                >
                                    <Icon icon="solar:check-circle-line-duotone" height="20" width="20" />
                                </v-btn>
                            </template>
                        </v-list-item>
                    </template>
                </v-list>
            </perfect-scrollbar>

            <!-- Footer -->
            <div class="py-4 px-6 text-center border-t">
                <v-btn
                    color="primary"
                    size="large"
                    rounded="pill"
                    block
                    variant="tonal"
                    :disabled="!hasUnread"
                    :loading="loading"
                    @click="handleMarkAllAsRead"
                >
                    <Icon icon="solar:check-read-line-duotone" height="20" width="20" class="mr-2" />
                    Marcar todas como lida
                </v-btn>
            </div>
        </v-sheet>
    </v-menu>
</template>

<style scoped>
.bg-grey100 {
    background-color: rgba(var(--v-theme-primary), 0.08) !important;
}

.border-t {
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.notification-item--clickable {
    cursor: pointer;
}

.notify {
    position: absolute;
    top: -8px;
    right: -8px;
}

.heartbit {
    position: absolute;
    top: -1px;
    right: -1px;
    height: 12px;
    width: 12px;
    z-index: 10;
    border: 3px solid rgb(var(--v-theme-error));
    border-radius: 70px;
    animation: heartbit 1s ease-out;
    animation-iteration-count: infinite;
}

.point {
    width: 6px;
    height: 6px;
    border-radius: 30px;
    background-color: rgb(var(--v-theme-error));
    position: absolute;
    right: 2px;
    top: 2px;
}

@keyframes heartbit {
    0% {
        transform: scale(0);
        opacity: 0;
    }

    25% {
        transform: scale(0.1);
        opacity: 0.1;
    }

    50% {
        transform: scale(0.5);
        opacity: 0.3;
    }

    75% {
        transform: scale(0.8);
        opacity: 0.5;
    }

    100% {
        transform: scale(1);
        opacity: 0;
    }
}
</style>
